import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getVapidKey, subscribePush, testNotification, getEnrollments, fetchTimetableEvents, bulkEnroll } from '../lib/api';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Sun, Moon, Bell, Check, BookOpen, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { startOfWeek, addDays } from 'date-fns';
import { Skeleton } from '../components/ui/skeleton';

// Reuse same translation from EventCard
const TYPE_LABELS = {
  'Vorlesung': 'Lecture', 'Seminar': 'Seminar', 'Praktikum': 'Lab',
  'Konsultation': 'Consultation', 'Tutorium': 'Tutorial',
  'Übung': 'Exercise', 'Klausur': 'Exam',
};
function translateType(t) {
  if (!t) return '';
  const match = Object.keys(TYPE_LABELS).find(k => t.startsWith(k));
  return TYPE_LABELS[match] || t;
}

export default function SettingsPage({ onBack }) {
  const { user, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  // Enrollment editing
  const [enrollExpanded, setEnrollExpanded] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [enrolledNames, setEnrolledNames] = useState(new Set());
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showAll, setShowAll] = useState(() => localStorage.getItem('tu_show_all_subjects') === 'true');

  const settings = user?.settings || {};

  useEffect(() => {
    if ('Notification' in window) {
      setNotifEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Load enrolled names
  useEffect(() => {
    getEnrollments().then(res => {
      setEnrolledNames(new Set((res.data || []).map(e => e.event_name)));
    }).catch(() => {});
  }, []);

  const loadCourses = useCallback(async () => {
    if (!settings.category_type_id || !settings.category_id || allCourses.length > 0) return;
    setCoursesLoading(true);
    try {
      const now = new Date();
      const ws = startOfWeek(now, { weekStartsOn: 1 });
      const we = addDays(ws, 13);
      const { data } = await fetchTimetableEvents(settings.category_type_id, settings.category_id, ws.toISOString(), we.toISOString());
      const seen = new Set();
      const unique = [];
      for (const ev of (data.events || [])) {
        if (!seen.has(ev.name)) {
          seen.add(ev.name);
          unique.push(ev);
        }
      }
      unique.sort((a, b) => a.name.localeCompare(b.name));
      setAllCourses(unique);
    } catch {
    } finally {
      setCoursesLoading(false);
    }
  }, [settings.category_type_id, settings.category_id, allCourses.length]);

  const handleExpandEnroll = () => {
    const next = !enrollExpanded;
    setEnrollExpanded(next);
    if (next) loadCourses();
  };

  const toggleCourse = (name) => {
    setEnrolledNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSaveEnrollments = async () => {
    setSaving(true);
    try {
      await bulkEnroll([...enrolledNames]);
      setSaveMsg('Saved!');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const reg = await navigator.serviceWorker.ready;
        const { data } = await getVapidKey();
        const subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
        const subJson = subscription.toJSON();
        await subscribePush({ endpoint: subJson.endpoint, keys: subJson.keys });
        setNotifEnabled(true);
      }
    } catch (err) {
      console.error('Notification setup failed', err);
    } finally {
      setNotifLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 rounded-sm" data-testid="settings-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-lg font-bold font-['Outfit'] tracking-tight">Settings</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-6 space-y-6 pb-12">
        {/* Program */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Program</h3>
          <div className="border rounded-sm p-4 bg-card">
            <p className="text-base font-semibold font-['Outfit']" data-testid="current-program">{settings.category_name || 'Master RCS SS'}</p>
          </div>
        </section>

        {/* Enrolled Courses - Editable */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Enrolled Courses</h3>
          <div className="border rounded-sm bg-card">
            <button
              onClick={handleExpandEnroll}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
              data-testid="expand-enrollments"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span className="text-sm font-medium">{enrolledNames.size} course{enrolledNames.size !== 1 ? 's' : ''} enrolled</span>
              </div>
              <div className="flex items-center gap-2">
                {saveMsg && <span className="text-xs text-emerald-500 font-medium flex items-center gap-1"><Check className="w-3 h-3" />{saveMsg}</span>}
                {enrollExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </button>

            {enrollExpanded && (
              <div className="border-t px-4 py-3">
                {coursesLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 rounded-sm" />)}
                  </div>
                ) : (
                  <>
                    <div className="space-y-1 max-h-[400px] overflow-y-auto">
                      {allCourses.map(ev => (
                        <button
                          key={ev.name}
                          onClick={() => toggleCourse(ev.name)}
                          data-testid={`settings-course-${ev.name.replace(/\s+/g, '-').toLowerCase()}`}
                          className={`w-full text-left rounded-sm p-2.5 flex items-center gap-3 transition-all duration-150
                            ${enrolledNames.has(ev.name)
                              ? 'bg-primary/5 border border-primary/30'
                              : 'hover:bg-muted/50 border border-transparent'
                            }`}
                        >
                          <Checkbox checked={enrolledNames.has(ev.name)} className="pointer-events-none" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{ev.name}</p>
                            <p className="text-[11px] text-muted-foreground">{translateType(ev.type)}{ev.professor ? ` | ${ev.professor}` : ''}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    <Button onClick={handleSaveEnrollments} disabled={saving} className="w-full mt-3 rounded-sm h-9 text-sm" data-testid="save-enrollments-btn">
                      {saving ? 'Saving...' : 'Save Enrollments'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Theme */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Appearance</h3>
          <div className="border rounded-sm bg-card divide-y">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-2">
                {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="text-sm font-medium">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={toggleTheme} className="rounded-sm h-8 text-xs" data-testid="toggle-theme">
                {theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
              </Button>
            </div>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-2">
                {showAll ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                <div>
                  <span className="text-sm font-medium">Show All Subjects</span>
                  <p className="text-xs text-muted-foreground">Show every course, even those not enrolled</p>
                </div>
              </div>
              <Button
                variant={showAll ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  const next = !showAll;
                  setShowAll(next);
                  localStorage.setItem('tu_show_all_subjects', next);
                }}
                className="rounded-sm h-8 text-xs"
                data-testid="toggle-show-all"
              >
                {showAll ? 'On' : 'Off'}
              </Button>
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Notifications</h3>
          <div className="border rounded-sm p-4 bg-card space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                <div>
                  <span className="text-sm font-medium">Push Notifications</span>
                  <p className="text-xs text-muted-foreground">30 min before enrolled classes</p>
                </div>
              </div>
              {notifEnabled ? (
                <span className="text-xs font-bold text-emerald-500 uppercase tracking-wider">Enabled</span>
              ) : (
                <Button onClick={handleEnableNotifications} disabled={notifLoading} size="sm" className="rounded-sm h-8 text-xs" data-testid="enable-notifications">
                  {notifLoading ? 'Enabling...' : 'Enable'}
                </Button>
              )}
            </div>
            {notifEnabled && (
              <Button variant="outline" size="sm" onClick={() => testNotification()} className="rounded-sm h-8 text-xs" data-testid="test-notification">
                Send Test Notification
              </Button>
            )}
          </div>
        </section>

        {/* Account */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Account</h3>
          <div className="border rounded-sm p-4 bg-card">
            <p className="text-sm" data-testid="user-email">{user?.email}</p>
            <p className="text-xs text-muted-foreground mt-1">{user?.name}</p>
          </div>
        </section>

        <section>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">About</h3>
          <div className="border rounded-sm p-4 bg-card">
            <p className="text-xs text-muted-foreground">TU Ilmenau Timetable PWA v1.0</p>
            <p className="text-xs text-muted-foreground mt-1">Data: opentimetable.tu-ilmenau.de</p>
          </div>
        </section>
      </main>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}
